import { TextField } from '@tunasong/ui-lib'
import { Record } from '@tunasong/icons'
import type { Track } from '@tunasong/schemas'
import { disableAutocomplete, getColorCSS, useCachedValue } from '@tunasong/ui-lib'
import cn from 'classnames'
import type { ChangeEvent, FC, HTMLAttributes, MouseEvent } from 'react'
import React, { useCallback } from 'react'
import { useMixer } from '../../hooks/index.js'
import { Meter } from '../../meter.js'
import { useTrackControl } from '../hooks/track-control.js'
import { ChannelButton } from './channel-button.js'
import { ChannelPan } from './channel-pan.js'
import { ChannelVolume } from './channel-volume.js'
import styles from './channel.module.css'

export interface ChannelProps {
  className?: string
  track: Track
  selected?: boolean
  onClick?(ev: MouseEvent): void
  onChange?(track: Track): void
}

interface ChannelContainerProps extends HTMLAttributes<HTMLDivElement> {
  isSelected?: boolean
}

const ChannelContainer = ({ isSelected, ...props }: ChannelContainerProps) => (
  <div
    {...props}
    className={cn(styles.container, isSelected && styles['container--selected'], props.className)}
    style={props.style}
  />
)

const ChannelRow = (props: HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className={cn(styles.row, props.className)} style={props.style} />
)

export const Channel: FC<ChannelProps> = props => {
  const { className, track, selected, onChange, onClick } = props
  const color = track.color ?? 'grey'
  const mixer = useMixer()

  const onUpdateTrack = (props: Partial<Track>) => onChange && onChange({ ...track, ...props })
  const { toggleMute, toggleSolo, toggleArm, onChangeVolume, onChangePan, pan, volume, channel } = useTrackControl({
    track,
    onUpdate: onUpdateTrack,
  })

  const [name, setName] = useCachedValue(track.name)
  const handleChange = useCallback(
    ({ target: { value } }: ChangeEvent<HTMLInputElement>) =>
      () =>
        setName(value),
    [setName]
  )
  const handleName = useCallback(
    (ev: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
      onChange && onChange({ ...track, name: ev.target.value ?? 'Untitled' }),
    [onChange, track]
  )

  /** @todo we need a better way to route input */
  const recordInput = mixer?.getBus('mic')

  return (
    <ChannelContainer
      style={{ color: getColorCSS(color) }}
      className={className}
      isSelected={selected}
      onClick={onClick}
    >
      <ChannelRow>
        <div>
          <ChannelButton selected={channel?.muted} onClick={toggleMute}>
            M
          </ChannelButton>
          <ChannelButton selected={channel?.solo} onClick={toggleSolo}>
            S
          </ChannelButton>
          <ChannelButton selected={channel?.armed} onClick={toggleArm}>
            <Record />
          </ChannelButton>
        </div>
        <TextField
          variant="standard"
          className={styles.nameField}
          inputProps={{
            ...disableAutocomplete,
            className: styles.nameInput,
          }}
          value={name}
          onChange={handleChange}
          onBlur={handleName}
        />
      </ChannelRow>
      <ChannelRow>
        <ChannelVolume volume={volume} onChangeVolume={onChangeVolume} />
      </ChannelRow>
      <ChannelRow>
        <ChannelPan pan={pan} onPan={onChangePan} />
      </ChannelRow>
      {channel?.armed && recordInput && (
        <ChannelRow>
          <Meter
            className={styles.meterRow}
            controller={recordInput}
          />
        </ChannelRow>
      )}
    </ChannelContainer>
  )
}

export default Channel
